<script>
    import { onMount } from "svelte";

    import { isMobile, user } from "../../stores";

    import { fly } from "svelte/transition";

    import MbsItemDescription from "../MBSItems/MBSItemDescription.svelte";

    import {
        SearchInput,
        SvelteTable,
        PageLoading,
        ResetFilterLink,
    } from "../../Components/Common/index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        getNumber,
        getText,
        goToURL,
        replaceURL,
        getAmountInCurrency,
        showError,
        encodeSearchParams,
        decodeSearchParams,
    } from "../../Utils/utilFunctions.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params = {};

    let isLoading = false,
        returnMBSItemsArr = [],
        filters = {},
        page = 0,
        pageSize = 25,
        count,
        showFilters = false;

    const columns = [
        {
            key: "itemCode",
            title: "Item Code",
            headerClass:
                "text-left px-3 sortable-header-decoration rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.itemCode),
        },
        {
            key: "mbsItemCode",
            title: "MBS Code",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => (v.mbsItemNum ? getText(v.mbsItemNum) : "-"),
        },
        {
            key: "amaCode",
            title: "AMA Code",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.amaCode),
        },
        {
            key: "amaUnits",
            title: "AMA Units",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getNumber(v.amaUnits),
        },
        {
            key: "infoType",
            title: "Info Type",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.infoType, "-"),
        },
        {
            key: "group",
            title: "Info Group",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.infoGroup, "-"),
        },
        {
            key: "rebateAMA",
            title: "Rebate",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getAmountInCurrency(v.rebateAMA),
        },
        {
            key: "infoShortTitle",
            title: "Short Title",
            headerClass: "text-left px-3 sortable-header-decoration",
            class: "text-left px-3 cursor-pointer",
            renderValue: (v) => getText(v.infoShortTitle, "-"),
        },
        {
            key: "description",
            title: "Description",
            headerClass:
                "text-left px-3 sortable-header-decoration rounded-tr-xl min-w-[360px]",
            class: "text-left px-3 cursor-pointer min-w-[360px]",
            renderComponent: {
                component: MbsItemDescription,
                props: {},
            },
            csvContent: (v) => `${v.description}`,
        },
    ];

    const searchFilters = [
        {
            key: "search",
            name: "Search",
            filterKey: "search",
        },
        {
            key: "itemCode",
            name: "Item Code",
            filterKey: "filter[itemCode]",
        },
        {
            key: "amaCode",
            name: "AMA Code",
            filterKey: "filter[amaCode]",
        },
        {
            key: "group",
            name: "Group",
            filterKey: "filter[group]",
        },
        {
            key: "description",
            name: "Description",
            filterKey: "filter[description]",
        },
        {
            key: "infoShortTitle",
            name: "Short Title",
            filterKey: "filter[infoShortTitle]",
        },
    ];

    $: fixedFilters = [searchFilters[0], searchFilters[1]];

    $: collapsibleFilters = [...searchFilters.slice(2)];

    const sortableHeaderKeys = ["itemCode", "description", "infoShortTitle"];

    $: isUserEnableViewAccountInvoice = $user.role >= 20;

    onMount(() => {
        if (params.query) updateSearchParamsByQuery(params.query);
        loadASAItems(page * pageSize, pageSize, _getSearchParams());
    });

    const updateSearchParamsByQuery = async (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("sort="))
                    filters.sort = arr[i].split("=")[1];

                searchFilters.forEach((sf) => {
                    if (arr[i].includes(sf.filterKey))
                        filters[sf.key] = arr[i].split("=")[1];
                });
            }

            updateURL();
        }
    };

    const loadASAItems = async (start, limit, params) => {
        isLoading = true;

        if (params.sort) params.sort = params.sort.toLowerCase();

        await apiMethods.getMethod
            .getASAItems(start, limit, params)
            .then((res) => handleASAItems(res))
            .then(() => _updateHeaderSortIcon());

        isLoading = false;
    };

    const handleFilterSelect = () => {
        page = 0;
        updateURL();
        loadASAItems(0, pageSize, _getSearchParams());
    };

    const changeFirstTooltipToButton = () => {
        setTimeout(() => {
            const firstDescription =
                document.getElementsByClassName("tooltipPlacehodler")[0];
            if (firstDescription) {
                firstDescription.className += " tooltip-bottom";
            }
        }, 100);
    };

    const updateURL = () => {
        replaceURL(
            `/ASAItems/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadASAItems(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadASAItems(0, newPageSize, _getSearchParams());
    };

    const handleClickCell = ({ target: { className } }, rowData, key) => {
        if (
            rowData.id &&
            isUserEnableViewAccountInvoice &&
            key != "description"
        )
            goToURL(`/ASAItems/view/${rowData.itemCode}`);
    };

    const handleClickCol = ({ target: { className } }, colData, key) => {
        if (sortableHeaderKeys.includes(key)) {
            if (filters.sort == key) filters.sort = `${key}Desc`;
            else filters.sort = key;

            updateURL();
            loadASAItems(0, pageSize, _getSearchParams());
        }
    };

    const handleASAItems = (res) => {
        if (res.error) showError(res.error);
        else {
            returnMBSItemsArr = [...res.results];
            count = res.total;
        }
        changeFirstTooltipToButton();
    };

    const _updateHeaderSortIcon = () => {
        let thRow = document.getElementsByTagName("th");
        const params = _getSearchParams();

        if (params) {
            setTimeout(() => {
                const upSpan = document.createElement("span");
                const downSpan = document.createElement("span");
                upSpan.innerHTML = `<i class="fas fa-sort-alpha-down" />`;
                upSpan.style = "font-size: 10px;";
                downSpan.innerHTML = `<i class="fas fa-sort-alpha-up-alt" />`;
                downSpan.style = "font-size: 10px;";

                Array.from(thRow).forEach((row) => {
                    if (
                        (params.sort == "itemCode" &&
                            row.childNodes[0].data == "Item Code") ||
                        (params.sort == "description" &&
                            row.childNodes[0].data == "Description") ||
                        (params.sort == "infoShortTitle" &&
                            row.childNodes[0].data == "Short Title")
                    )
                        row.appendChild(upSpan);
                    else if (
                        (params.sort == "itemCodeDesc" &&
                            row.childNodes[0].data == "Item Code") ||
                        (params.sort == "descriptionDesc" &&
                            row.childNodes[0].data == "Description") ||
                        (params.sort == "infoShortTitleDesc" &&
                            row.childNodes[0].data == "Short Title")
                    )
                        row.appendChild(downSpan);
                });
            }, 0);
        }
    };

    const _getSearchParams = () => {
        let params = { sort: filters.sort ?? "itemCode" };

        searchFilters.forEach((sf) => {
            if (filters[sf.key]) params[sf.filterKey] = filters[sf.key];
        });

        return params;
    };

    const _getParamsString = () => {
        let str = "";
        const params = _getSearchParams();

        if (params) {
            if (params.sort) str += `&sort=${params.sort}`;

            searchFilters.forEach((sf) => {
                if (params[sf.filterKey])
                    str += `&${sf.filterKey}=` + params[sf.filterKey];
            });
        }

        return encodeSearchParams(str);
    };

    const toggleFilters = () => {
        showFilters = !showFilters;
    };

    const filterTransition = (node, args) => ($isMobile ? fly(node, args) : "");
</script>

<div class="flex flex-col flex-1 gap-y-4">
    {#if $isMobile}
        <button
            class="btn btn-accent btn-sm w-28 flex flex-row gap-x-3"
            on:click={toggleFilters}
        >
            Filters
            <i
                style="font-size:20px"
                class={showFilters ? "fa arrow-up" : "fa arrow-down"}
                >&#xf104;
            </i>
        </button>

        {#if showFilters}
            <div class="flex gap-4 flex-wrap items-end">
                {#each fixedFilters as searchFilter}
                    <SearchInput
                        bind:value={filters[searchFilter.key]}
                        title={searchFilter.name}
                        inputFunc={handleFilterSelect}
                        isLong
                    />
                {/each}
                {#each collapsibleFilters as searchFilter}
                    <SearchInput
                        bind:value={filters[searchFilter.key]}
                        title={searchFilter.name}
                        inputFunc={handleFilterSelect}
                        isLong
                    />
                {/each}
                <ResetFilterLink
                    bind:filter={filters}
                    onResetFilter={handleFilterSelect}
                />
            </div>
        {/if}
    {:else}
        <div
            class="flex gap-4 flex-wrap items-end"
            in:filterTransition={{ duration: 200, y: -100 }}
        >
            {#each fixedFilters as searchFilter}
                <SearchInput
                    bind:value={filters[searchFilter.key]}
                    title={searchFilter.name}
                    inputFunc={handleFilterSelect}
                    isLong
                />
            {/each}
            {#each collapsibleFilters as searchFilter}
                <SearchInput
                    bind:value={filters[searchFilter.key]}
                    title={searchFilter.name}
                    inputFunc={handleFilterSelect}
                    isLong
                />
            {/each}
            <ResetFilterLink
                bind:filter={filters}
                onResetFilter={handleFilterSelect}
            />
        </div>
    {/if}

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isLoading}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-scroll overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnMBSItemsArr}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                    customRow="align-middle"
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnMBSItemsArr}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnMBSItemsArr,
            fileName: "ASA Items",
            allDataApi: async () =>
                await apiMethods.getMethod.getASAItems(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>
